import * as React from 'react';
import Flex from 'antd/lib/flex';
import Text from 'antd/lib/typography/Text';
import { Link } from 'react-router-dom';
import {
    getNavSectionTitleStyle,
    getNavDropdownMenuStyle,
    navFlexItemStyle,
    releaseMetaTextStyle,
} from '../../styles/globalStyles';
import { useSidebarColors } from '../../styles/themeMode';
import { HoverableNavItem } from '../shared/HoverableNavItem';
import { createReleaseOverviewRoute } from '../../../helpers/routeHelpers';
import { ReleaseStatus } from '../../../types';
import { StatusNodeDot } from '../../StatusNode';
import { PhasedRolloutBadge, rollbackStripeBackground, useReleaseNodeDisplay } from '../useReleaseNodeDisplay';

export interface ReleaseListItem {
    id: string;
    releaseId: string;
    version: string;
    displayableVersion: string;
    status: ReleaseStatus;
}

interface VersionItemProps {
    orgId: string;
    appId: string;
    appEntityId: string;
    release: ReleaseListItem;
    isSelected: boolean;
    onSelect: () => void;
}

const VersionItem: React.FC<VersionItemProps> = ({ orgId, appId, appEntityId, release, isSelected, onSelect }) => {
    const sc = useSidebarColors();
    const display = useReleaseNodeDisplay(release.id, appEntityId);
    const textColor = display.isSkipped ? sc.textMuted : sc.text;
    return (
        <Link
            to={createReleaseOverviewRoute(orgId, appId, release.releaseId)}
            onClick={onSelect}
            style={{ textDecoration: 'none' }}
        >
            <HoverableNavItem
                isSelected={isSelected}
                style={display.isRollback ? rollbackStripeBackground(sc.dropdownHoverBg, sc.dropdownBg) : undefined}
            >
                <Flex align="center" gap={12} style={navFlexItemStyle}>
                    <StatusNodeDot
                        statusColor={display.dotColor}
                        halfFilled={display.showPhasedHalfFilled}
                        size={12}
                        outerBorderColor="transparent"
                        outerBorderWidth={0}
                    />
                    <Text
                        style={{
                            ...navFlexItemStyle,
                            color: textColor,
                            fontWeight: isSelected ? 600 : 400,
                            textDecoration: display.isSkipped ? 'line-through' : 'none',
                        }}
                        ellipsis
                    >
                        {release.displayableVersion}
                    </Text>
                    {display.icon}
                    {display.isPhasedRolloutInProgress && (
                        <PhasedRolloutBadge display={display} color={textColor} textStyle={releaseMetaTextStyle} />
                    )}
                    {display.isRollback && <Text style={{ ...releaseMetaTextStyle, color: textColor }}>Rollback</Text>}
                </Flex>
            </HoverableNavItem>
        </Link>
    );
};

export interface VersionDropdownProps {
    orgId: string;
    appId: string;
    /** Redux app entity key, used to resolve per-release phased/rollback display data. */
    appEntityId: string;
    currentReleaseId: string;
    upcomingReleases: ReleaseListItem[];
    currentReleases: ReleaseListItem[];
    completedReleases: ReleaseListItem[];
    onSelect: () => void;
}

export const VersionDropdown: React.FC<VersionDropdownProps> = ({
    orgId,
    appId,
    appEntityId,
    currentReleaseId,
    upcomingReleases,
    currentReleases,
    completedReleases,
    onSelect,
}) => {
    const sc = useSidebarColors();
    const renderReleaseGroup = (title: string, releases: typeof upcomingReleases) => {
        if (!releases || releases.length === 0) return null;

        return (
            <div style={{ marginBottom: 16 }}>
                <Text style={getNavSectionTitleStyle(sc)}>{title}</Text>
                <div style={{ paddingTop: 8 }}>
                    {releases.map((release) => (
                        <VersionItem
                            key={release.id}
                            orgId={orgId}
                            appId={appId}
                            appEntityId={appEntityId}
                            release={release}
                            isSelected={release.id === currentReleaseId}
                            onSelect={onSelect}
                        />
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div
            style={{
                ...getNavDropdownMenuStyle(sc),
                minWidth: '18rem',
                maxHeight: '400px',
                overflowY: 'auto',
                padding: '12px 0',
            }}
        >
            {renderReleaseGroup('Upcoming', upcomingReleases)}
            {renderReleaseGroup('Active', currentReleases)}
            {renderReleaseGroup('Completed', completedReleases.slice(0, 10))}
        </div>
    );
};
